import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { auth, db } from "../../firebase";
import { doc, setDoc, writeBatch, collection } from "firebase/firestore";
import styleClasses from "./css/MyPage.module.scss";

const API_SERVER_URL = "https://music-api-134893551192.asia-northeast3.run.app/api/albums/youtube"; // 🔹 Cloud Run API URL
const SPOTIFY_API_SERVER_URL = "https://music-api-134893551192.asia-northeast3.run.app/api/albums/spotify";

function ImportAlbum() {
  const { attribute } = useParams(); // 🔹 URL에서 released / unreleased 가져오기
  const [channelUrl, setChannelUrl] = useState(""); // 채널 URL 입력값
  const [artistUrl, setArtistUrl] = useState("");   // 스포티파이 아티스트 URL 입력값
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uid, setUid] = useState(null);
  const [authToken, setAuthToken] = useState(""); // 🔹 Firebase Auth JWT 토큰 저장

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUid(user.uid);
        const token = await user.getIdToken(); // 🔥 Firebase Auth JWT 토큰 가져오기
        setAuthToken(token);
      }
    });

    return () => unsubscribe();
  }, []);

  // ✅ 유튜브에서 앨범 데이터 가져오기 (Cloud Run API 호출)
  const fetchAlbumsFromYouTube = async () => {
    if (!channelUrl) {
      alert("유튜브 채널 URL을 입력하세요.");
      return;
    }

    setLoading(true);
    try {
      console.log("🎤 입력된 채널 URL:", channelUrl);

      // 🔹 Cloud Run API 호출
      const response = await axios.get(API_SERVER_URL, {
        params: { channelUrl }, // 🔹 API에 channelUrl 전달
        headers: {
          Authorization: `Bearer ${authToken}`, // 🔹 Firebase Auth 토큰 포함
        },
      });

      console.log("🎶 Cloud Run API 응답 데이터:", response.data);
      setAlbums(response.data.albums); // 🔥 API 응답에서 albums 데이터 설정
    } catch (error) {
      console.error("❌ Cloud Run API 호출 실패:", error);
      alert("앨범 검색 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // 스포티파이 앨범 가져오기 함수
  const fetchAlbumsFromSpotify = async () => {
    if (!artistUrl) {
      alert("스포티파이 아티스트 URL을 입력하세요.");
      return;
    }
    setLoading(true);
    try {
      console.log("🎤 입력된 스포티파이 아티스트 URL:", artistUrl);
      const response = await axios.get(SPOTIFY_API_SERVER_URL, {
        params: { artistUrl },
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      console.log("🎶 스포티파이 API 응답 데이터:", response.data);
      setAlbums(response.data.albums);
    } catch (error) {
      console.error("❌ 스포티파이 API 호출 실패:", error);
      alert("앨범 검색 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Firestore에 앨범 추가 함수
  const handleAddAlbum = async (album) => {
    if (!uid) {
      alert("로그인이 필요합니다.");
      return;
    }

    try {
      const albumRef = doc(db, "users_data", uid, "released", album.id);
      const albumData = {
        cover: album.cover,
        title: album.title,
        year: album.year,
        artist: album.artist,
        createdAt: new Date().toISOString(),
      };

      await setDoc(albumRef, albumData);

      // 트랙 리스트 처리: 각 트랙에 번호를 붙임 (01, 02, …)
      const updatedTracks = album.tracks.map((track, index) => ({
        id: (index + 1).toString().padStart(2, "0"),
        title: track.title,
        url: track.link,
        roles: track.credits && track.credits.length > 0 ? track.credits.join(", ") : ""
      }));

      // 트랙 정보를 하위 컬렉션 'tracks'에 저장 (writeBatch 사용)
      const batch = writeBatch(db);
      const tracksRef = collection(db, "users_data", uid, "released", album.id, "tracks");
      updatedTracks.forEach((track) => {
        const trackDocRef = doc(tracksRef, track.id);
        batch.set(trackDocRef, track);
      });
      await batch.commit();      


      alert(`"${album.title}" 앨범이 추가되었습니다.`);
    } catch (error) {
      console.error("앨범 저장 실패:", error);
      alert("앨범 추가 중 오류가 발생했습니다.");
    }
  };

  return (
    <div className={styleClasses.addAlbumPage}>
      <h1>앨범 가져오기</h1>

      {/* 유튜브 앨범 가져오기 섹션 */}
      <div className={styleClasses.searchBox}>
        <h2>유튜브 앨범 가져오기</h2>
        <p>'아티스트명 - 주제(Topic)' 형태의 유튜브 채널 URL을 입력하면, 해당 아티스트의 등록된 모든 앨범을 가져옵니다.</p>
        <p>유튜브 뮤직의 아티스트 페이지에서 공유 URL을 복사하시는 것이 가장 정확합니다.</p>
        <p>예) https://www.youtube.com/channel/[채널ID]</p>
        <p>또는 https://music.youtube.com/channel/[채널ID]</p>
        <input
          type="text"
          placeholder="유튜브 채널 URL 입력"
          value={channelUrl}
          onChange={(e) => setChannelUrl(e.target.value)}
        />
        <button onClick={fetchAlbumsFromYouTube} disabled={loading}>
          {loading ? "검색 중..." : "앨범 검색"}
        </button>
      </div>

      {/* 스포티파이 앨범 가져오기 섹션 */}
      <div className={styleClasses.searchBox}>
        <h2>스포티파이 앨범 가져오기</h2>
        <p>스포티파이 아티스트 URL을 입력하면, 해당 아티스트의 모든 앨범을 가져옵니다.</p>
        <p>예) https://open.spotify.com/artist/[아티스트ID]</p>
        <input
          type="text"
          placeholder="스포티파이 아티스트 URL 입력"
          value={artistUrl}
          onChange={(e) => setArtistUrl(e.target.value)}
        />
        <button onClick={fetchAlbumsFromSpotify} disabled={loading}>
          {loading ? "검색 중..." : "앨범 검색"}
        </button>
      </div>

      <div className={styleClasses.albumList}>
        {albums.length > 0 ? (
          albums.map((album) => (
            <div key={album.id} className={styleClasses.albumCard}>
              {/* 앨범 정보 (썸네일 + 앨범명 + 발매일) */}
              <div className={styleClasses.albumHeader}>
                <img src={album.cover} alt="앨범 커버" />
                <p>앨범명 : {album.title}</p>
                <p>발매일 : {album.year}</p>
                <p>아티스트 : {album.artist}</p>
              </div>

              {/* 트랙 리스트 */}
              {album.tracks.map((track) => (
                <div key={track.id} className={styleClasses.trackItem}>
                  <a href={track.link} target="_blank" rel="noopener noreferrer">
                    🎵 {track.title}
                  </a>
                  {track.credits.length > 0 && <p>크레딧 : {track.credits.join(", ")}</p>}
                </div>
              ))}
              <button onClick={() => handleAddAlbum(album)}>아카이브에 추가</button>
            </div>
          ))
        ) : (
          <p>검색 결과가 없습니다.</p>
        )}
      </div>
    </div>
  );
}

export default ImportAlbum;